// src/components/ActivityPopup.jsx
// Popup a due tab per le attività Monti Alburni (e future aree).
// Tab 1: info base (categoria, comune, contatti)
// Tab 2: info aggiuntive caricate da /api/poi-submissions/by-location

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContactLinks from './ContactLinks';
import { isMobileNumber, splitPhones, buildWhatsappUrl, trackPoiContact } from '../utils/contactUtils';
import OSMInfoDialog from './POI/OSMInfoDialog';
import POIEnrichmentForm from './POI/POIEnrichmentForm';

const API_BASE = process.env.REACT_APP_API_URL_NODE || '';

const CATEGORIES = {
    agriturismo: { emoji: "🌾" },
    lodging:      { emoji: "🏨" },
    rifugio:      { emoji: "🏕️" },
    camp_site:    { emoji: "⛺" },
    food:         { emoji: "🍽️" },
    outdoor_guide:{ emoji: "🧭" },
    local_products:{ emoji: "🧀" },
    info_point:   { emoji: "ℹ️" },
    sport:        { emoji: "🏄" },
    culture:      { emoji: "🏛️" },
    osservatorio: { emoji: "🔭" },
    oasi:         { emoji: "🦅" },
    natura:       { emoji: "🪨" },
};

export default function ActivityPopup({ item, placement = "map_popup", onShowNearby }) {
    const { t, i18n } = useTranslation();
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [additional, setAdditional] = useState(null);   // null = in caricamento, false = non trovato, object = dati
    const [showOSM, setShowOSM] = useState(false);
    const [showEnrichForm, setShowEnrichForm] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const cat = CATEGORIES[item.category];
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${item.location.lat},${item.location.lng}`;

    // Costruisce un GeoJSON Feature compatibile con OSMInfoDialog e POIEnrichmentForm
    const osmPoi = item.id ? {
        type: 'Feature',
        id: item.id,
        properties: { name: item.name, id: item.id },
        geometry: { type: 'Point', coordinates: [item.location.lng, item.location.lat] },
    } : null;

    // Carica i dati aggiuntivi al mount per determinare etichetta bottone Aggiungi/Aggiorna
    useEffect(() => {
        fetch(`${API_BASE}/api/poi-submissions/by-location?lat=${item.location.lat}&lng=${item.location.lng}`)
            .then(r => r.json())
            .then(result => setAdditional(result.found && result.data ? result.data : false))
            .catch(() => setAdditional(false));
    }, [item.location.lat, item.location.lng]);

    const hasAdditional = additional !== null && additional !== false;
    // Nasconde il tab se sappiamo che non ci sono dati
    const showAdditionalTab = additional !== false;

    // Handler per "Nei dintorni"
    function handleNearby() {
        if (typeof onShowNearby === "function") {
            onShowNearby();
        } else {
            navigate(`/nearby?lat=${item.location.lat}&lng=${item.location.lng}&radius=3000&from=activity&name=${encodeURIComponent(item.name || '')}`);
        }
    }

    return (
        <div style={{ minWidth: 220, maxWidth: 280, fontFamily: 'sans-serif', fontSize: 13 }}>
            {showOSM && osmPoi && (
                <OSMInfoDialog poi={osmPoi} onClose={() => setShowOSM(false)} />
            )}
            {showEnrichForm && osmPoi && (
                <POIEnrichmentForm poi={osmPoi} onClose={() => setShowEnrichForm(false)} />
            )}

            {/* Header */}
            <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 15, display: 'block' }}>{item.name ?? '—'}</strong>
                {cat && (
                    <span style={{ fontSize: 12, color: '#666' }}>
                        {cat.emoji} {t(`cat_${item.category}`)}
                        {item.municipality && ` · ${item.municipality}`}
                    </span>
                )}
                {item.notOnOSM && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#333' }}>
                        ⚠️ {t('not_on_osm')}
                    </span>
                )}
            </div>

            {/* Tab bar */}
            {showAdditionalTab && (
                <div style={{ display: 'flex', gap: 4, marginBottom: 8, borderBottom: '1px solid #ddd' }}>
                    {['info', 'additional'].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} style={{
                            flex: 1, padding: '5px 8px', background: 'none', cursor: 'pointer',
                            border: 'none', borderBottom: activeTab === tab ? '2px solid #2196F3' : '2px solid transparent',
                            color: activeTab === tab ? '#2196F3' : '#666',
                            fontWeight: activeTab === tab ? 600 : 400, fontSize: 12,
                        }}>
                            {tab === 'info' ? `📋 ${t('tab_info')}` : `ℹ️ ${t('tab_additional')}`}
                        </button>
                    ))}
                </div>
            )}

            {/* Tab: Info base */}
            {activeTab === 'info' && (
                <div>
                    {item.address?.street && (
                        <p style={rowStyle}>📍 {item.address.street}{item.address.city ? `, ${item.address.city}` : ''}</p>
                    )}
                    <ContactLinks
                        contacts={item.contacts}
                        entityId={item.id}
                        entityType="activity"
                        placement={placement}
                    />
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" style={btnStyle('#4285F4')}>
                            🗺️ Maps
                        </a>
                        {/* Bottone copia link */}
                        <button
                            onClick={async () => {
                                let link = window.location.origin;
                                const parts = pathname.split('/');
                                const region = parts[1] === 'region' ? parts[2] : null;
                                if (pathname.startsWith('/region/campania/monti-alburni') && item.id) {
                                    link += `/region/campania/monti-alburni#poi-${item.id}`;
                                } else if (region && item.id) {
                                    link += `/region/${region}/poi/${item.id}`;
                                } else {
                                    link += `/?lat=${item.location.lat}&lng=${item.location.lng}`;
                                }
                                try {
                                    await navigator.clipboard.writeText(link);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 1200);
                                } catch (e) {
                                    setCopied(false);
                                }
                            }}
                            style={btnStyle('#1976d2')}
                        >
                            {copied ? `✅ ${t('copied')}` : `🔗 ${t('copy_link')}`}
                        </button>
                        {osmPoi && (
                            <button onClick={() => setShowOSM(true)} style={btnStyle('#7ebc6f')}>
                                📋 {t('map_info')}
                            </button>
                        )}
                        {osmPoi && (
                            <button onClick={() => setShowEnrichForm(true)} style={btnStyle('#4CAF50')}>
                                {hasAdditional ? `✏️ ${t('update')}` : `✏️ ${t('add')}`}
                            </button>
                        )}
                        <button
                            onClick={() => {
                                const parts = pathname.split('/');
                                const region = parts[1] === 'region' ? parts[2] : null;
                                if (!region) return;
                                const poiName = item.name ? `&name=${encodeURIComponent(item.name)}` : '';
                                navigate(`/region/${region}/trekkings?poi=${item.location.lat},${item.location.lng}&zoom=15${poiName}`);
                            }}
                            style={btnStyle('#388E3C')}
                        >
                            🥾 {t('trails')}
                        </button>
                        <button
                            onClick={handleNearby}
                            style={btnStyle('#8e44ad')}
                        >
                            🌄 {t('nearby')}
                        </button>
                    </div>
                    <p style={{ fontSize: 10, color: '#aaa', marginTop: 8 }}>📊 AI Hiking</p>
                </div>
            )}

            {/* Tab: Info aggiuntive */}
            {activeTab === 'additional' && (
                <div>
                    {additional === null && (
                        <div style={{ textAlign: 'center', padding: '15px 0', color: '#666' }}>
                            <p style={{ fontSize: 12, margin: 0 }}>{t('loading_additional')}</p>
                        </div>
                    )}
                    {hasAdditional && <AdditionalContent data={additional} item={item} />}
                </div>
            )}
        </div>
    );
}

// ─── Contenuto tab aggiuntivo ─────────────────────────────────────────────────

function AdditionalContent({ data, item }) {
    const { t, i18n } = useTranslation();
    const osmid = String(item.id || '').replace(/^(node|way|relation)\//, '');
    const lat = item.location.lat;
    const lng = item.location.lng;

    const dateLocale = i18n.language === 'en' ? 'en-GB' : 'it-IT';

    return (
        <div>
            {data.photos?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                    <strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        📸 {t('photos')} ({data.photos.length})
                    </strong>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                        {data.photos.map((photo, i) => (
                            <a key={i} href={photo.url} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'block', borderRadius: 4, overflow: 'hidden' }}>
                                <img src={photo.thumbnailUrl} alt={`${t('photos')} ${i + 1}`} loading="lazy"
                                    referrerPolicy="no-referrer"
                                    style={{ width: '100%', height: 60, objectFit: 'cover', display: 'block' }}
                                    onError={e => { e.target.style.display = 'none'; }} />
                            </a>
                        ))}
                    </div>
                </div>
            )}
            {data.description && (
                <div style={{ marginBottom: 8, padding: 8, background: '#f9f9f9', borderRadius: 4, borderLeft: '2px solid #4CAF50' }}>
                    <strong style={{ color: '#4CAF50', fontSize: 12 }}>📝 {t('description')}</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, lineHeight: 1.5 }}>{data.description}</p>
                </div>
            )}
            {data.tipologia && (
                <p style={rowStyle}><strong>🏷️ {t('tipologia')}:</strong> {data.tipologia}</p>
            )}
            {data.servizi_offerti?.length > 0 && (
                <div style={{ margin: '6px 0' }}>
                    <strong style={{ fontSize: 12 }}>✨ {t('services')}:</strong>
                    <ul style={{ margin: '4px 0', paddingLeft: 16, fontSize: 12 }}>
                        {data.servizi_offerti.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                </div>
            )}
            {data.come_raggiungere && (
                <div style={{ margin: '6px 0', padding: 8, background: '#f9f9f9', borderRadius: 4 }}>
                    <strong style={{ fontSize: 12 }}>🗺️ {t('how_to_reach')}</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: 12 }}>{data.come_raggiungere}</p>
                </div>
            )}
            {data.orari && (
                <p style={rowStyle}><strong>🕐 {t('hours')}:</strong> {data.orari}</p>
            )}
            {data.periodo_apertura && (
                <p style={rowStyle}><strong>📅 {t('period')}:</strong> {data.periodo_apertura}</p>
            )}
            {data.pernottamento && (
                <p style={rowStyle}><strong>🛏️ {t('overnight')}:</strong> {data.pernottamento}</p>
            )}
            {data.tariffe_indicative && (
                <p style={rowStyle}><strong>💰 {t('rates')}:</strong> {data.tariffe_indicative}</p>
            )}
            {data.phone && <AdditionalPhones raw={data.phone} osmid={osmid} lat={lat} lng={lng} />}
            {data.email && (
                <p style={rowStyle}><strong>📧 {t('email')}:</strong>{' '}
                    <button onClick={() => { window.location.href = `mailto:${data.email}`; trackPoiContact('email', osmid, lat, lng); }} style={linkBtnStyle}>
                        {data.email}
                    </button>
                </p>
            )}
            {data.website && (
                <p style={rowStyle}><strong>🌐 {t('website')}:</strong>{' '}
                    <button onClick={() => { window.open(data.website, '_blank', 'noopener,noreferrer'); trackPoiContact('website', osmid, lat, lng); }} style={linkBtnStyle}>
                        {t('visit_website')}
                    </button>
                </p>
            )}
            {data.instagram && (
                <p style={rowStyle}><strong>📸 Instagram:</strong>{' '}
                    <a href={data.instagram} target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3' }}>{t('profile')}</a>
                </p>
            )}
            {data.facebook && (
                <p style={rowStyle}><strong>👥 Facebook:</strong>{' '}
                    <a href={data.facebook} target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3' }}>{t('page')}</a>
                </p>
            )}
            {data.booking_system && (
                <p style={rowStyle}><strong>📅 {t('bookings')}:</strong>{' '}
                    <a href={data.booking_system} target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3' }}>{t('booking_system')}</a>
                </p>
            )}
            <p style={{ fontSize: 10, color: '#aaa', marginTop: 10, paddingTop: 8, borderTop: '1px solid #eee' }}>
                ✏️ {t('added_on')} {new Date(data.submitted_at).toLocaleDateString(dateLocale)}
            </p>
        </div>
    );
}

function AdditionalPhones({ raw, osmid, lat, lng }) {
    const { t } = useTranslation();
    const parts = splitPhones(raw);
    let mobileShown = false;
    return (
        <>
            {parts.map((part, i) => {
                const mobile = isMobileNumber(part);
                const waUrl = mobile && !mobileShown ? buildWhatsappUrl(part, '') : null;
                if (mobile && !mobileShown) mobileShown = true;
                return (
                    <React.Fragment key={i}>
                        {waUrl && (
                            <p style={rowStyle}><strong>💬 WhatsApp:</strong>{' '}
                                <button onClick={() => window.open(waUrl, '_blank', 'noopener,noreferrer')} style={{ ...linkBtnStyle, color: '#25D366' }}>
                                    {t('write_whatsapp')}
                                </button>
                            </p>
                        )}
                        <p style={rowStyle}>
                            <strong>{mobile ? `📱 ${t('mobile_phone')}` : `📞 ${t('phone')}`}:</strong>{' '}
                            <button onClick={() => { window.location.href = `tel:${part}`; trackPoiContact('phone', osmid, lat, lng); }} style={linkBtnStyle}>
                                {part}
                            </button>
                        </p>
                    </React.Fragment>
                );
            })}
        </>
    );
}

// ─── Stili ────────────────────────────────────────────────────────────────────

const rowStyle = { margin: '4px 0', fontSize: 12 };

const linkBtnStyle = {
    background: 'none', border: 'none', padding: 0,
    color: '#2196F3', fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
};

const btnStyle = (bg) => ({
    display: 'inline-block', padding: '5px 10px', background: bg,
    color: 'white', textDecoration: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500,
    cursor: 'pointer', border: 'none',
});
